import React, { useState, useEffect, useCallback } from 'react';
import Map from 'react-map-gl';
import maplibregl from 'maplibre-gl';
import {
  ArcLayer, DeckGL, FlyToInterpolator, H3HexagonLayer, LineLayer
} from 'deck.gl';

import { rgb } from 'd3';

import TimelineScrubber from './TimelineScrubber';
import LogoPanel from './LogoPanel';
import ShiftHint from './ShiftHint';
import RightPanel from './RightPanel';
import { ATTRIBUTE_OPTIONS, IS_IPAD } from './utils';
import LeftPanel from './LeftPanel';
import { useSessionContext } from './SessionContext';
import MobilePanel from './MobilePanel';

const ELEVATION_SCALE = 100000;
const INITIAL_VIEWSTATE = {
  longitude: -95.7129,
  latitude: 37.0902,
  zoom: 4,
  bearing: 0,
  pitch: 50,
};

export default function App() {
  const {
    hexagons, selectedLocation, nearbyGauges, selectedAttribute, selectedDateIndex, hexagonOpacity,
    setHexagonOpacity, isMobile
  } = useSessionContext();

  // used to keep track of what was going on before a search result was selected
  const [originalHexagonOpacity, setOriginalHexagonOpacity] = useState();
  const [originalViewState, setOriginalViewState] = useState();

  const [hasAdjustedAngle, setHasAdjustedAngle] = useState(false);
  const [viewState, setViewState] = useState(INITIAL_VIEWSTATE);

  // when a location (from search) finishes loading, center it on the screen with an animation
  useEffect(() => {
    if (!selectedLocation) {
      if (originalHexagonOpacity) setHexagonOpacity(originalHexagonOpacity);
      if (originalViewState) {
        setViewState({
          ...(originalViewState.zoom < 6 ? originalViewState : INITIAL_VIEWSTATE),
          transitionDuration: 500,
          transitionInterpolator: new FlyToInterpolator()
        });
      }
      setOriginalHexagonOpacity();
      setOriginalViewState();
      return;
    }

    setOriginalHexagonOpacity(hexagonOpacity);
    setOriginalViewState(viewState);
    const { lat, lng } = selectedLocation;
    setHexagonOpacity(1);
    const latitudeAdjustment = isMobile ? 0.15 : 0;
    setViewState((current) => ({
      ...current,
      longitude: Number(lng),
      latitude: Number(lat) + latitudeAdjustment,
      zoom: 9,
      bearing: 0,
      pitch: 60,
      transitionDuration: 500,
      transitionInterpolator: new FlyToInterpolator()
    }));
  }, [selectedLocation]);

  const getHexagonElevation = useCallback((hexagon) => {
    switch (selectedAttribute) {
      case 'wli':
        return hexagon.timeline[selectedDateIndex]
          ? hexagon.timeline[selectedDateIndex].wliNorm : null;
      case 'gaugeCount':
        return hexagon.gaugeCountNorm;
      default:
        return null;
    }
  }, [selectedAttribute, selectedDateIndex]);

  const getHexagonFillColor = useCallback((hexagon) => {
    const { colorMapInterpolator } = ATTRIBUTE_OPTIONS[selectedAttribute];
    const level = getHexagonElevation(hexagon);
    if (level === null) return [0, 0, 0, 0];
    const color = colorMapInterpolator(level);
    const { r, g, b } = rgb(color);
    return [r, g, b];
  }, [selectedAttribute, selectedDateIndex, getHexagonElevation]);

  const getHexagonTooltip = useCallback((hexagon) => {
    if (getHexagonElevation(hexagon) === null) return null;

    const pluralizedGauges = hexagon.gaugeCount === 1 ? 'gauge' : 'gauges';

    const shouldShowWLI = (
      selectedAttribute === 'wli' && hexagon.timeline[selectedDateIndex] !== null
    );
    const wliLabel = shouldShowWLI ? `
      <span>Water Level Index: <b>${hexagon.timeline[selectedDateIndex].wli}</b></span>
      <br />
    ` : '';

    return {
      html: `
        <div>
          ${wliLabel}
          <span>${hexagon.gaugeCount} ${pluralizedGauges}</span>
        </div>
      `,
      style: {
        color: 'var(--gray0)',
        backgroundColor: 'rgba(28, 28, 30, 0.4)',
        backdropFilter: 'blur(10px)',
        '-webkit-backdrop-filter': 'blur(10px)'
      }
    };
  }, [selectedAttribute, selectedDateIndex, getHexagonElevation]);

  const getTooltip = useCallback((item) => {
    // for search result "beacon" mouseover
    if (item.name) {
      return {
        html: selectedLocation.name,
        style: {
          'max-width': '200px'
        }
      };
    }

    // for hexagon mouseover
    return getHexagonTooltip(item);
  }, [selectedLocation, getHexagonTooltip,]);

  // used to get the start/end points of the nearby gauges arcs,
  // so they don't get covered by the hexagons
  const getHexagonPosition = useCallback((lat, lng, hexagonCode) => {
    const targetHexagon = hexagons.find((hexagon) => hexagon.hexagonCode === hexagonCode);
    const elevation = hexagonOpacity < 50
      ? 0 // hexagon is mostly transparent, so it's safe to put stuff on the ground
      : getHexagonElevation(targetHexagon) * ELEVATION_SCALE;
    return [lng, lat, elevation];
  }, [hexagons, hexagonOpacity, getHexagonElevation]);

  const hexagonLayer = new H3HexagonLayer({
    id: 'h3-hexagon-layer',
    data: hexagons,
    pickable: true,
    stroked: true,
    filled: true,
    opacity: hexagonOpacity / 100,
    extruded: true,
    getHexagon: (hexagon) => hexagon.hexagonCode,
    getElevation: getHexagonElevation,
    getFillColor: getHexagonFillColor,
    coverage: 0.999, // prevents depth-fighting when opacity < 1
    updateTriggers: {
      getFillColor: [selectedAttribute, selectedDateIndex],
      getElevation: [selectedAttribute, selectedDateIndex]
    },
    getLineColor: [255, 255, 255],
    lineWidthMinPixels: 2,
    elevationScale: ELEVATION_SCALE
  });

  const selectedLocationLayer = new LineLayer({
    id: 'search-result-layer',
    data: selectedLocation ? [selectedLocation] : [],
    getSourcePosition: ({ lng, lat }) => [Number(lng), Number(lat)],
    getTargetPosition: ({ lng, lat }) => [Number(lng), Number(lat), 100000000],
    getColor: [199, 199, 201],
    getWidth: 5,
    pickable: true
  });

  const nearbyGaugesLayer = new ArcLayer({
    id: 'nearby-gauges-layer',
    data: selectedLocation ? nearbyGauges : [],
    opacity: 0.8,
    getSourcePosition: () => {
      const { lat, lng, hexagonCode } = selectedLocation;
      return getHexagonPosition(lat, lng, hexagonCode);
    },
    getTargetPosition: ({ lat, lng, hexagonCode }) => getHexagonPosition(lat, lng, hexagonCode),
    updateTriggers: {
      getSourcePosition: [getHexagonPosition],
      getTargetPosition: [getHexagonPosition]
    },
    getSourceColor: [196, 173, 116],
    getTargetColor: [161, 54, 157],
    getWidth: 3,
    pickable: false
  });

  return (
    <>
      <DeckGL
        viewState={viewState}
        controller={{ touchRotate: true }}
        onViewStateChange={({ viewState: newViewState }) => {
          // update tracked view state
          setViewState({ ...newViewState, transitionDuration: 0 });

          // keep track of whether "Hold down shift to rotate" hint can be hidden
          if (newViewState.bearing !== 0) setHasAdjustedAngle(true);
        }}
        layers={[nearbyGaugesLayer, selectedLocationLayer, hexagonLayer]}
        getTooltip={({ object }) => object && getTooltip(object)}
      >
        <Map
          mapLib={maplibregl}
          attributionControl={false}
          mapStyle="https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json"
        />
      </DeckGL>
      <div className="overlay-container top">
        <LeftPanel />
        <RightPanel />
      </div>
      <div
        style={{ bottom: (IS_IPAD && window.innerWidth <= 900) ? '12px' : 0 }}
        className="overlay-container bottom"
      >
        <LogoPanel />
        {selectedAttribute === 'wli' && (
          <TimelineScrubber />
        )}
        <ShiftHint isVisible={!hasAdjustedAngle} />
      </div>
      <MobilePanel />
    </>
  );
}
